import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, CheckCircle2, AlertCircle, Building2 } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const STATUS_TONE = {
    pending_approval: { tone: "bg-amber-50 border-amber-200 text-amber-900", icon: Clock, title: "Application under review" },
    approved: { tone: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: CheckCircle2, title: "Application approved" },
    rejected: { tone: "bg-rose-50 border-rose-200 text-rose-900", icon: AlertCircle, title: "Application needs more info" },
    suspended: { tone: "bg-zinc-100 border-zinc-300 text-zinc-700", icon: AlertCircle, title: "Account suspended" },
};

const BusinessPendingContent = () => {
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/b2b/me")
            .then(({ data }) => {
                setAccount(data);
                if (data?.status === "approved") navigate("/business/dashboard", { replace: true });
            })
            .catch(() => navigate("/business/register", { replace: true }))
            .finally(() => setLoading(false));
    }, [navigate]);

    if (loading) return <div className="h-48 skeleton rounded-3xl" />;
    if (!account) return null;
    const cfg = STATUS_TONE[account.status] || STATUS_TONE.pending_approval;
    const Icon = cfg.icon;

    return (
        <div className="space-y-6" data-testid="b2b-pending-page">
            <div className={`rounded-3xl border p-6 ${cfg.tone}`}>
                <Icon className="h-8 w-8" />
                <h2 className="mt-3 text-xl font-semibold">{cfg.title}</h2>
                <p className="mt-1 text-sm opacity-90">
                    {account.status === "pending_approval" && "We'll verify your GSTIN and drug licence within 24 business hours. You'll receive an in-app + email notification when approved."}
                    {account.status === "rejected" && (account.rejected_reason || "Please re-check the information and resubmit.")}
                    {account.status === "suspended" && "Please reach out to your account manager."}
                </p>
                {account.status === "rejected" && (
                    <Link to="/business/register" className="mt-4 inline-block rounded-full bg-white px-4 py-2 text-sm font-medium text-rose-700 ring-1 ring-rose-200">
                        Update & resubmit →
                    </Link>
                )}
            </div>

            <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft">
                <p className="label-uppercase">Submitted details</p>
                <h3 className="mt-1 text-base font-semibold text-zinc-900">{account.legal_name}</h3>
                <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                    <div><dt className="text-zinc-400">Organisation</dt><dd className="text-zinc-800 capitalize">{(account.organization_type || "").replace("_", " ")}</dd></div>
                    <div><dt className="text-zinc-400">GSTIN</dt><dd className="font-mono text-zinc-800">{account.gstin}</dd></div>
                    <div><dt className="text-zinc-400">Drug Licence</dt><dd className="font-mono text-zinc-800">{account.drug_license_no}</dd></div>
                    <div><dt className="text-zinc-400">Contact</dt><dd className="text-zinc-800">{account.primary_contact_name}</dd></div>
                </dl>
            </div>
        </div>
    );
};

const BusinessPending = () => (
    <Layout>
        <section className="mx-auto max-w-2xl px-6 py-10 lg:px-12">
            <header className="mb-6">
                <p className="label-uppercase"><Building2 className="-mt-0.5 mr-1 inline h-3 w-3" /> B2B portal</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Application status</h1>
            </header>
            <AuthGuard>
                <BusinessPendingContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default BusinessPending;
